import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const TokenContainer = styled(motion.div)`
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 40px;
  margin-bottom: 40px;
  border: 2px solid rgba(34, 197, 94, 0.3);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.4);
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 25px;
    margin-bottom: 30px;
    border-radius: 15px;
  }
  
  @media (max-width: 480px) {
    padding: 20px;
    margin-bottom: 20px;
    border-radius: 12px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #22c55e, #16a34a, #22c55e);
    background-size: 200% 100%;
    animation: shimmer 3s ease-in-out infinite;
  }

  @keyframes shimmer {
    0%, 100% { background-position: 200% 0; }
    50% { background-position: -200% 0; }
  }
`;

const TokenTitle = styled.h3`
  color: #22c55e;
  font-size: 2rem;
  margin-bottom: 30px;
  font-weight: 800;
  text-align: center;
  text-shadow: 0 2px 10px rgba(34, 197, 94, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 25px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
    margin-bottom: 20px;
  }
`;

const TokenGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 18px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
    padding: 0 10px;
  }
  
  @media (max-width: 480px) {
    gap: 15px;
    padding: 0 5px;
  }
`;

const TokenCard = styled(motion.div)`
  background: rgba(34, 197, 94, 0.08);
  padding: 25px;
  border-radius: 18px;
  text-align: center;
  border: 2px solid rgba(34, 197, 94, 0.2);
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  min-height: 130px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  will-change: auto;
  transform: translateZ(0);
  
  @media (max-width: 1024px) {
    padding: 22px;
    min-height: 125px;
  }
  
  @media (max-width: 768px) {
    padding: 20px;
    min-height: 120px;
    border-radius: 15px;
  }
  
  @media (max-width: 480px) {
    padding: 15px;
    min-height: 100px;
    border-radius: 12px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(34, 197, 94, 0.1), transparent);
    transition: left 0.6s ease;
  }

  &:hover {
    transform: translateY(-8px) scale(1.02);
    background: rgba(34, 197, 94, 0.15);
    box-shadow: 0 15px 35px rgba(34, 197, 94, 0.3);
    border-color: rgba(34, 197, 94, 0.4);

    &::before {
      left: 100%;
    }
  }
`;

const TokenLabel = styled.div`
  font-weight: 700;
  margin-bottom: 12px;
  color: #22c55e;
  font-size: 1.1rem;
  text-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
  
  @media (max-width: 1024px) {
    font-size: 1.05rem;
    margin-bottom: 10px;
  }
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 15px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin-bottom: 12px;
  }
`;

const TokenValue = styled.div`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.9);
  word-break: break-all;
  line-height: 1.4;
  font-weight: 500;
  
  @media (max-width: 1024px) {
    font-size: 0.9rem;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.5;
  }
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
  }
`;

const StatusBadge = styled.div`
  display: inline-block;
  background: linear-gradient(45deg, #22c55e, #16a34a);
  color: white;
  padding: 12px 20px;
  border-radius: 25px;
  font-weight: 700;
  font-size: 1rem;
  margin-top: 15px;
  box-shadow: 0 6px 20px rgba(34, 197, 94, 0.4);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(34, 197, 94, 0.5);
  }
`;

const ContractAddress = styled.div`
  font-family: 'Courier New', monospace;
  background: rgba(0, 0, 0, 0.4);
  padding: 15px 50px 15px 15px;
  border-radius: 12px;
  margin-top: 15px;
  font-size: 1rem;
  font-weight: 700;
  word-break: break-all;
  color: #22c55e;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(34, 197, 94, 0.2);
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(0, 0, 0, 0.6);
    transform: translateY(-2px);
    border-color: rgba(34, 197, 94, 0.4);
    box-shadow: 0 8px 25px rgba(34, 197, 94, 0.2);
  }
  
  @media (max-width: 768px) {
    padding: 12px 45px 12px 12px;
    font-size: 0.8rem;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 10px 40px 10px 10px;
    font-size: 0.75rem;
    border-radius: 8px;
  }
`;

const CopyButton = styled(motion.button)`
  position: absolute;
  top: 8px;
  right: 8px;
  background: linear-gradient(45deg, rgba(34, 197, 94, 0.9), rgba(22, 163, 74, 0.9));
  border: none;
  color: white;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);

  &:hover {
    background: linear-gradient(45deg, rgba(34, 197, 94, 1), rgba(22, 163, 74, 1));
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4);
  }
  
  @media (max-width: 768px) {
    top: 6px;
    right: 6px;
    padding: 5px 8px;
    font-size: 0.7rem;
    border-radius: 5px;
  }
  
  @media (max-width: 480px) {
    top: 5px;
    right: 5px;
    padding: 4px 6px;
    font-size: 0.65rem;
    border-radius: 4px;
  }
`;

const CopySuccess = styled(motion.div)`
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(-50%);
  background: #22c55e;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 4px 15px rgba(34, 197, 94, 0.3);
  z-index: 10;
`;

function TokenInfo() {
  const [copySuccess, setCopySuccess] = useState(false);

  const copyContractAddress = () => {
    navigator.clipboard.writeText('0x7ea7bae59b389aad6fdd8d7ee48e114e35c1114a');
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  return (
    <TokenContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.4 }}
    >
      <TokenTitle>🔥 XWF Token 信息</TokenTitle>
      <TokenGrid>
        <TokenCard
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <TokenLabel>📋 XWF CA</TokenLabel>
          <ContractAddress onClick={copyContractAddress}>
            0x7ea7bae59b389aad6fdd8d7ee48e114e35c1114a
            <CopyButton
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={(e) => {
                e.stopPropagation();
                copyContractAddress();
              }}
            >
              📋
            </CopyButton>
            {copySuccess && (
              <CopySuccess
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                ✅ 已复制到剪贴板！
              </CopySuccess>
            )}
          </ContractAddress>
        </TokenCard>

        <TokenCard
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <TokenLabel>⛓️ BlockChain</TokenLabel>
          <TokenValue>OKX Xlayer</TokenValue>
        </TokenCard>

        {/* <TokenCard
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <TokenLabel>🚀 XWF 项目状态</TokenLabel>
          <StatusBadge>即将起飞 🚀</StatusBadge>
        </TokenCard>

        <TokenCard
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <TokenLabel>💎 XWF 社区热度</TokenLabel>
          <TokenValue>🔥 超热门 🔥</TokenValue>
        </TokenCard> */}
      </TokenGrid>
    </TokenContainer>
  );
}

export default TokenInfo;
